import { useMutation, type UseMutationOptions } from "react-query";
import { isDev } from "config";
import { api, ApiError } from "utils/api";
import { sleep } from "utils/hooks";
import type { ImageToUpload, UploadedImage } from "../types";

/**
 * Given an array of content type strings, fetch an array of signed URLs to upload a file of each type
 */
const getImageUploadUrls = async (types: string[]) =>
  api.get<{ urls: string[] }>(
    `/upload_url?${new URLSearchParams(
      types.map((type) => ["contentTypes", type]),
    )}`,
  );

/**
 * Mutation to get batched array of signed upload URLs, useful for multi-file upload form
 */
const useImageUploadUrls = () => useMutation(getImageUploadUrls);

/**
 * Upload a given file to the provided URL, resolving in the file and its final URL
 *
 * If no URL is provided, fetch a new signed URL according to the file's type.
 */
const putImageUpload = async (img: ImageToUpload): Promise<UploadedImage> => {
  const url = img.url ?? (await getImageUploadUrls([img.file.type])).urls[0];

  // TODO: remove this, just for testing upload errors
  if (isDev && img.file.name.includes("fail")) {
    await sleep(7000);
    throw new ApiError("Image upload failed", 500);
  }

  const resp = await fetch(url, {
    method: "PUT",
    body: img.file,
  });

  if (!resp.ok) {
    throw new ApiError("Image upload failed", resp.status);
  }

  return {
    file: img.file,
    // Remove query string from AWS S3 URL
    url: url.indexOf("?") > -1 ? url.slice(0, url.indexOf("?")) : url,
  };
};

/**
 * Mutation to upload a single file
 */
const useImageUpload = (
  options?: UseMutationOptions<UploadedImage, unknown, ImageToUpload>,
) => {
  const {
    data: upload,
    mutate: runUpload,
    ...uploadQuery
  } = useMutation(putImageUpload, options);

  return {
    runUpload,
    upload,
    ...uploadQuery,
  };
};

export { useImageUpload, useImageUploadUrls };
